import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslate } from "helpers";

import {
  deleteFleetDocumentAPI,
  getFleetDocumentsAPI,
} from "services/api/fleet/vehicles/documents/api";
import { initialFleetVehicleDocumentSearchForm } from "services/api/fleet/vehicles/documents/consts";
import { DataWithPagination } from "services/api";
import { initialDataWithPagination } from "services/api/consts";
import { Pagination } from "services/search";
import {
  FleetDocumentRow,
  FleetDocumentSearchFormValues,
} from "services/api/fleet/vehicles/documents/types";

import { Breadcrumb } from "ui/components/Breadcrumb";
import { Dialog } from "ui/components/Dialog";
import { Loader } from "ui/components/Loader";
import { NoResults } from "ui/components/NoResults";
import { PageHeader } from "ui/components/PageHeader";
import { PageTitle } from "ui/components/PageTitle";
import { useSelectedItemsChanges } from "ui/components/Table/hooks";

import { DOCUMENT_SEARCH_PAGE_TITLE } from "ui/components/PageTitle/consts";
import { Routes as FinanceRoutes } from "ui/modules/finance/navigation";

import DocumentSearchFilters from "./components/DocumentSearchFilters/DocumentSearchFilters";
import DocumentSearchResults from "./components/DocumentSearchResults/DocumentSearchResults";
import { buildVehicleDetailRoute } from "./components/DocumentSearchResults/consts";
import { DocumentSearchPageCmp, DocumentSearchPageProps } from "./types";

const DocumentSearchPage: DocumentSearchPageCmp = (
  _props: DocumentSearchPageProps
) => {
  const { t } = useTranslate();
  const navigate = useNavigate();

  const [searchResults, setSearchResults] = useState<
    DataWithPagination<FleetDocumentRow>
  >(initialDataWithPagination);
  const [filters, setFilters] = useState<FleetDocumentSearchFormValues>(
    initialFleetVehicleDocumentSearchForm
  );
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [documentId, setDocumentId] = useState<string>("");
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    fetchSearchResult(searchResults.pagination);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSearchResult = async (
    pagination: Pagination,
    values: FleetDocumentSearchFormValues = filters
  ) => {
    setIsLoading(true);
    try {
      const results = await getFleetDocumentsAPI(
        pagination,
        values.search,
        undefined,
        undefined,
        values.documentTypeId ?? undefined,
        values.createdFrom,
        values.createdTo
      );
      setSearchResults(results);
    } catch {
      setSearchResults(initialDataWithPagination);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearchChange = useCallback(
    (formValues: FleetDocumentSearchFormValues) => {
      setFilters(formValues);
      fetchSearchResult(searchResults.pagination, formValues);
    },
    [searchResults.pagination]
  );

  const handlePaginationChange = (newPagination: Pagination) => {
    fetchSearchResult(newPagination, filters);
  };

  const handleSelectedChange = useSelectedItemsChanges(
    selectedRows,
    setSelectedRows
  );

  const handleVehicleClick = useCallback(
    (event: React.MouseEvent, vehicleId: string) => {
      event.stopPropagation();
      navigate(buildVehicleDetailRoute(vehicleId));
    },
    [navigate]
  );

  const handleDeleteClick = (
    event: React.MouseEvent<HTMLSpanElement, MouseEvent>,
    id: string
  ) => {
    event.stopPropagation();
    event.preventDefault();
    setDocumentId(id);
    setOpenDialog(true);
  };

  const handleDeleteDialogConfirm = useCallback(async () => {
    if (!documentId) return;
    try {
      const deleted = await deleteFleetDocumentAPI(documentId, t);
      if (deleted) {
        await fetchSearchResult(searchResults.pagination, filters);
      }
    } finally {
      setOpenDialog(false);
      setDocumentId("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, t, searchResults.pagination, filters]);

  const handleDeleteDialogClose = useCallback(() => {
    setDocumentId("");
    setOpenDialog(false);
  }, []);

  const pageTitle = DOCUMENT_SEARCH_PAGE_TITLE;

  return (
    <>
      <div>
        <div className="bg-white rounded-t-md border border-b mb-2">
          <PageHeader>
            <PageTitle
              title={pageTitle.text}
              icon={pageTitle.icon}
              iconColor={pageTitle.iconColor}
            />
            <Breadcrumb />
          </PageHeader>
        </div>
        <div className="px-0 py-6 mx-[-1px] xl:px-6">
          <DocumentSearchFilters onSearchChange={handleSearchChange} />
          {isLoading && searchResults.data.length === 0 ? (
            <Loader isLoading={true}>{null}</Loader>
          ) : !isLoading && searchResults.pagination.totalPages === 0 ? (
            <NoResults message="Nema dokumenata u flotnoj bazi." />
          ) : (
            <DocumentSearchResults
              searchResults={searchResults}
              selectedRows={selectedRows}
              isLoading={isLoading}
              onPaginationChange={handlePaginationChange}
              onSelectedChange={handleSelectedChange}
              onDeleteClick={handleDeleteClick}
              onVehicleClick={handleVehicleClick}
            />
          )}
        </div>
      </div>
      <Dialog
        open={openDialog}
        title={t("admin.fleet.vehicle.document.dialog.delete.title")}
        message={t("admin.fleet.vehicle.document.dialog.delete.message")}
        confirmText={t("admin.fleet.vehicle.document.dialog.delete.confirmtext")}
        cancelText={t("admin.fleet.vehicle.document.dialog.delete.canceltext")}
        onConfirm={handleDeleteDialogConfirm}
        onCancel={handleDeleteDialogClose}
      />
    </>
  );
};

DocumentSearchPage.route = FinanceRoutes.DocumentSearchPageRouter;

export default DocumentSearchPage;
